import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popover,
  MenuItem,
  IconButton,
  Tooltip,
  Box,
  Avatar,
} from '@mui/material';
import PropTypes from 'prop-types';
import { Edit, Delete, CheckCircle, Block, PaymentsTwoTone } from '@mui/icons-material';

import { authContext } from '../../../routes';
import axiosInstance from '../../../api';
import Label from '../../../components/label';
import CustomSnackbar from '../../../components/alerts/CustomSnackbar';
import Iconify from '../../../components/iconify';
import SERVER_API_URL from '../../../config';
import UserDataGrid from '../../common/UserDataGrid';
import { ethDate } from '../../../utils/ethDate';

function AvatarCellRenderer(params) {
  const [openImage, setOpenImage] = useState(false);
  const { id } = params;
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const getAvatar = async () => {
      try {
        const response = await axiosInstance.get(`${SERVER_API_URL}/avatar/getAvatarById/${id}`, {
          responseType: 'blob',
        });
        if (response.status === 200) {
          const url = URL.createObjectURL(response.data);
          setAvatarUrl(url);
        }
      } catch (e) {
        setAvatarUrl(null);
      }
    };
    const getUserData = async () => {
      try {
        const response = await axiosInstance.post(`${SERVER_API_URL}/user/findOneById/${id}`);
        if (response.status === 200) {
          setUserData(response.data);
        }
      } catch (error) {
        // console.log(error)
      }
    };
    getAvatar();
    getUserData();
  }, [id]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={`መለያ: ${params.id}`} arrow>
          <IconButton onClick={() => setOpenImage(true)}>
            {<Avatar src={avatarUrl} alt={`${userData && userData.fullName} መገለጫ`} />}
          </IconButton>
        </Tooltip>
      </Box>
      <Dialog open={openImage} onClose={() => setOpenImage(false)}>
        {avatarUrl && <img src={avatarUrl} alt={`${userData && userData.fullName} መገለጫ`} />}
        {!avatarUrl && <img src="/assets/icon/empty_user_profile.png" alt={`${userData && userData.firstName} መገለጫ`} />}
        <DialogTitle>{userData && userData.fullName}</DialogTitle>
      </Dialog>
    </>
  );
}

export default function UserList({ userToBeUpdated, setUserToBeUpdated, setChurchActivatedOn }) {
  const { user } = useContext(authContext);
  const [userList, setUserList] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedUser, setSelectedUser] = useState();
  const [selectedUserId, setSelectedUserId] = useState();
  const [open, setOpen] = useState(null);
  const [notification, setNotification] = useState({});

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = async () => {
    setOpen(null);
  };

  const handleSelectedMenu = async (newStatus) => {
    if (open) {
      try {
        const response = await axiosInstance.patch(`${SERVER_API_URL}/user/updateStatus/${selectedUserId}`, {
          status: newStatus,
        });
        if (response.status === 200) {
          getAllUser();
          notification.message = 'ለውጦች በተሳካ ሁኔታ ተቀምጠዋል።';
          notification.severity = 'success';
        }
      } catch (e) {
        notification.message = 'ለውጦችን ማስቀመጥ አልተሳካም። እባክዎ ዳግም ይሞክሩ!';
        notification.severity = 'error';
      }
      setNotification(notification);
    }
    setOpen(false);
  };

  const columns = [
    {
      headerClassName: 'super-app-theme--header', field: 'id',
      width: 100,
      headerName: 'መለያ',
      renderCell: AvatarCellRenderer,
    },
    {
      headerClassName: 'super-app-theme--header', field: 'fullName',
      width: 300,
      headerName: 'ሙሉ ስም',
    },
    { headerClassName: 'super-app-theme--header', field: 'christianName', width: 200, headerName: 'የክርስትና ስም' },
    {
      headerClassName: 'super-app-theme--header', field: 'phone',
      width: 150,
      headerName: 'ስልክ ቁጥር',
      renderCell: (params) => (
        <>
          {!params.value && <Label sx={{ color: 'maroon' }}>ባዶ</Label>}
          {params.value}
        </>
      ),
    },
    { headerClassName: 'super-app-theme--header', field: 'gender', width: 100, headerName: 'ጾታ' },
    {
      headerClassName: 'super-app-theme--header', field: 'status',
      width: 100,
      headerName: 'ሁኔታ',
      renderCell: (params) => {
        const getStatusIcon = (status) => {
          switch (status) {
            case 'ንቁ':
              return (
                <Label color="success" sx={{ fontSize: '12px' }}>
                  <CheckCircle sx={{ fontSize: '14px' }} />
                  ንቁ
                  <Tooltip title="change status" arrow>
                    <IconButton
                      size="small"
                      color="inherit"
                      disabled={!(user && user.role.permissions.user.update)}
                      onClick={() => setSelectedUserId(params.id)}
                    >
                      <Iconify icon={'eva:more-vertical-fill'} onClick={handleOpenMenu} />
                    </IconButton>
                  </Tooltip>
                </Label>
              );
            case 'ታግዷል':
              return (
                <Label color="error" sx={{ fontSize: '12px' }}>
                  <Block sx={{ fontSize: '12px' }} />
                  ታግዷል
                  <Tooltip title="change status" arrow>
                    <IconButton
                      size="small"
                      color="inherit"
                      disabled={!(user && user.role.permissions.user.update)}
                      onClick={() => setSelectedUserId(params.id)}
                    >
                      <Iconify icon={'eva:more-vertical-fill'} onClick={handleOpenMenu} />
                    </IconButton>
                  </Tooltip>
                </Label>
              );
            default:
              return null;
          }
        };
        return <>{getStatusIcon(params.value)}</>;
      },
    },
    {
      headerClassName: 'super-app-theme--header', field: 'actions',
      width: 150,
      headerName: 'ድርጊቶች',
      renderCell: (params) => {
        const handlePayment = async () => {
          try {
            const response = await axiosInstance.post(`${SERVER_API_URL}/user/findOneById/${params.id}`);
            if (response.status === 200) {
              setUserToBeUpdated(response.data);
              setChurchActivatedOn('userPayment');
            }
          } catch (error) {
            // console.log(error)
          }
        };
        const handleDetail = async () => {
          try {
            const response = await axiosInstance.post(`${SERVER_API_URL}/user/findOneById/${params.id}`);
            if (response.status === 200) {
              setUserToBeUpdated(response.data);
              setChurchActivatedOn('userUpdate');
            }
          } catch (error) {
            // console.log(error)
          }
        };

        const handleDelete = async () => {
          try {
            const response = await axiosInstance.post(`${SERVER_API_URL}/user/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedUser(response.data);
              setOpenDelete(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };

        return (
          <>
            <Tooltip title="ክፍያ" color="info" arrow>
              <IconButton disabled={!(user && user.role.permissions.user.read)} onClick={handlePayment}>
                <PaymentsTwoTone sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="አርትዕ" color="warning" arrow>
              <IconButton disabled={!(user && user.role.permissions.user.update)} onClick={handleDetail}>
                <Edit sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="ሰርዝ" color="error" arrow>
              <IconButton onClick={handleDelete} disabled={!(user && user.role.permissions.user.delete)}>
                <Delete sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    { headerClassName: 'super-app-theme--header', field: 'church', width: 200, headerName: 'አጥቢያ', valueGetter: (params) => params.row.church.name },
    { headerClassName: 'super-app-theme--header', field: 'district', width: 200, headerName: 'ወረዳ', valueGetter: (params) => params.row.church.district.name },
    {
      headerClassName: 'super-app-theme--header', field: 'role',
      width: 150,
      headerName: 'ሚና',
      valueGetter: (params) => params.row.role.name,
    },
    {
      headerClassName: 'super-app-theme--header', field: 'category',
      width: 100,
      headerName: 'ምድብ',
      valueGetter: (params) => params.row.category.name,
    },
    {
      headerClassName: 'super-app-theme--header', field: 'position',
      width: 100,
      headerName: 'የቤተክርስትያን የስራ መደብ',
    },
    {
      headerClassName: 'super-app-theme--header', field: 'email',
      width: 200,
      headerName: 'ኢሜይል',
      renderCell: (params) => (
        <>
          {!params.value && <Label sx={{ color: 'maroon' }}>ባዶ</Label>}
          {params.value}
        </>
      ),
    },
    {
      headerClassName: 'super-app-theme--header', field: 'lastLoggedIn',
      headerName: 'መጨረሻ የገባበት ቀን',
      width: 180,
      renderCell: (params) => (
        <>
          {!params.value && <Label sx={{ color: 'maroon' }}>እስካሁን አልገባም።</Label>}
          {params.value && ethDate(params.value)}
        </>
      ),
    },
    {
      headerClassName: 'super-app-theme--header', field: 'createdAt',
      headerName: 'የተመዘገበበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
    {
      headerClassName: 'super-app-theme--header', field: 'updatedAt',
      headerName: 'የተስተካከለበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
  ];

  const handleOk = async () => {
    try {
      const response = await axiosInstance.delete(`${SERVER_API_URL}/user/deleteOneById/${selectedUser.id}`);
      if (response.status === 200) {
        getAllUser();
        setOpenDelete(false);
      }
    } catch (error) {
      // console.log(error)
    }
  };

  const getAllUser = useCallback(async () => {
    try {
      const response = await axiosInstance.post(
        `${SERVER_API_URL}/user/findManyByChurchId/${userToBeUpdated && userToBeUpdated[0].church.id}`
      );

      if (response.status === 200) {
        const rawUsers = response.data.filter((person) => person.id !== (user && user.id));
        setUserList(rawUsers);
      }
    } catch (error) {
      // console.log(error);
    }
  }, [user, userToBeUpdated]);

  useEffect(() => {
    getAllUser();
  }, [getAllUser]);

  return (
    <>
      {notification.message && <CustomSnackbar notification={notification} setNotification={setNotification} />}
      <UserDataGrid columns={columns} userList={userList} />
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <Box sx={{ backgroundColor: (theme) => theme.palette.mode === 'light' ? '#F9F9FB' : '#272424' }}>
          <DialogTitle>
            እርግጠኛ ንዎት ተጠቃሚ {selectedUser && selectedUser.firstName + selectedUser.lastName}ን ማጥፋት ትፈልጋሉ።
          </DialogTitle>
          <DialogContent>
            <DialogContentText>ከዚህ ተጠቃሚ ጋር የተያያዙ ሁሉም መረጃዎች ይጠፋሉ!</DialogContentText>
            <DialogContentText>ይህ እርምጃ ሊቀለበስ አይችልም።</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDelete(false)}>ተመለስ</Button>
            <Button color="error" variant="contained" startIcon={<Delete />} onClick={handleOk} autoFocus>
              ሰርዝ
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem sx={{ color: 'success.main' }} onClick={() => handleSelectedMenu('ንቁ')}>
          <CheckCircle sx={{ mr: 2 }} />
          ንቁ
        </MenuItem>
        <MenuItem sx={{ color: 'error.main' }} onClick={() => handleSelectedMenu('ታግዷል')}>
          <Block sx={{ mr: 2 }} />
          ታግዷል
        </MenuItem>
      </Popover>
    </>
  );
}

UserList.propTypes = {
  userToBeUpdated: PropTypes.array,
  setUserToBeUpdated: PropTypes.func,
  setChurchActivatedOn: PropTypes.func,
};
